import { action, computed, flow, makeObservable, observable, reaction } from 'mobx';

import { PlanVersionType } from 'src/store/comparison/types';
import type { EditingStore } from 'src/store/editing/editing-store';
import type { JobsStore } from 'src/store/jobs/jobs-store';
import type { NotificationsStore } from 'src/store/notifications-store/notifications-store';
import type { PlanVersionsService } from 'src/store/plan-version/plan-versions-service';
import {
  FULL_ACCESS_ROLE,
  GOPLAN_DRILLING_CARPET_GRAPH_EDIT,
  GOPLAN_DRILLING_CARPET_GRAPH_READ,
  GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT,
  GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT_ALIAS,
} from 'src/store/roles-store/roles';
import type { RolesStore } from 'src/store/roles-store/roles-store';

import { PlanType } from '../../features/drilling-chart/shared/plan-type';

import type { DrillingPlanChartStore } from './drilling-plan-chart.store';

export class HeaderStore {
  private readonly drillingPlanChartStore: DrillingPlanChartStore;
  private readonly notifications: NotificationsStore;
  private readonly planVersionsService: PlanVersionsService;
  private readonly rolesStore: RolesStore;
  readonly editing: EditingStore;
  readonly jobListStore: JobsStore;

  @observable displayChartType: PlanType;
  @observable isEditModeLoading = false;
  @observable private _isLoading = false;

  constructor(
    editing: EditingStore,
    notifications: NotificationsStore,
    drillingPlanChartStore: DrillingPlanChartStore,
    jobListStore: JobsStore,
    rolesStore: RolesStore,
    planVersionsService: PlanVersionsService,
  ) {
    this.editing = editing;
    this.notifications = notifications;
    this.drillingPlanChartStore = drillingPlanChartStore;
    this.jobListStore = jobListStore;
    this.rolesStore = rolesStore;
    this.planVersionsService = planVersionsService;

    this.displayChartType = (() => {
      if (rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_GRAPH_READ, GOPLAN_DRILLING_CARPET_GRAPH_EDIT])) {
        return PlanType.rigs;
      }

      return PlanType.wells;
    })();

    makeObservable(this);
  }

  @action.bound
  init() {
    const disposeEditing = reaction(
      () => this.isEditing,
      (isEditing: boolean) => {
        if (isEditing && this.displayChartType === PlanType.wells) {
          this.onDisplayChartTypeChange(PlanType.rigs);
        }
      },
    );

    return () => {
      disposeEditing();
    };
  }

  @computed
  get isEditing(): boolean {
    return this.editing.isEditing;
  }

  @computed
  get isLoading(): boolean {
    return this._isLoading || this.editing.isLoading || this.drillingPlanChartStore.isChartLoading;
  }

  @computed
  get isEditingBtnAvailable(): boolean {
    if (this.displayChartType === PlanType.wells) {
      return false;
    }

    if (this.planVersionsService.currentVersion?.data.isArchive) {
      return this.rolesStore.hasAccess([
        FULL_ACCESS_ROLE,
        GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT,
        GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT_ALIAS,
      ]);
    }

    if (this.planVersionsService.currentVersion?.data.versionType === PlanVersionType.current) {
      return false;
    }

    return this.rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_GRAPH_EDIT]);
  }

  @action.bound
  onDisplayChartTypeChange(type: PlanType): void {
    this.displayChartType = type;
  }

  @flow.bound
  async *onEdit(): Promise<void> {
    if (this.jobListStore.currentImportVersionJob) {
      return;
    }

    try {
      this.isEditModeLoading = true;

      await this.editing.enableEditing();
      yield;
    } catch {
      yield;

      this.notifications.showErrorMessageT('errors:failedToActivateEditMode');
    } finally {
      this.isEditModeLoading = false;
    }
  }

  @flow.bound
  async *onSave(): Promise<void> {
    try {
      this._isLoading = true;

      const isAllowedSave = await this.drillingPlanChartStore.checkConflicts();
      yield;

      if (!isAllowedSave) {
        this.notifications.showErrorMessageT('errors:failedToSaveChanges');
        return;
      }

      await this.editing.saveChanges(true);
      yield;
    } catch (e) {
      yield;

      console.error(e);
      this.notifications.showErrorMessageT('errors:failedToSaveChanges');
    } finally {
      this._isLoading = false;
    }
  }

  @flow.bound
  async *onCancel(): Promise<void> {
    try {
      this._isLoading = true;

      await this.editing.cancelEditing();
      yield;
    } catch (e) {
      yield;

      console.error(e);
      this.notifications.showErrorMessageT('errors:errorExitingEditMode');
    } finally {
      this._isLoading = false;
    }
  }

  @flow.bound
  async *onPublish(): Promise<void> {
    try {
      this._isLoading = true;
      const isAllowedPublish = await this.drillingPlanChartStore.checkConflicts();
      yield;

      if (!isAllowedPublish) {
        this.notifications.showErrorMessageT('errors:failedToPublishDraft');
        return;
      }

      await this.editing.publishChanges();
      yield;
    } catch (e) {
      yield;

      console.error(e);
      this.notifications.showErrorMessageT('errors:failedToPublishDraft');
    } finally {
      this._isLoading = false;
    }
  }
}
